import { useTranslator } from '@bluebeam/react-i18n';
import ConfirmModal from 'components/modals/ConfirmModal';
import { BannerContext } from 'components/Routes/BannerContext';
import { StdList, StdListBody, StdListHeader } from 'components/StdList';
import ExpandableTableRowContext from 'contexts/ExpandableTableRowContext';
import { List } from 'immutable';
import React, { ReactElement, useContext, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { CreateRFI } from 'records/interactions/CreateRFI';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { ModalState } from 'records/ModalState';
import { NewRFI } from 'records/NewRFI';
import { RFI } from 'records/RFI';
import { clearQueryParam } from 'utils/url';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import NoRFIItems from './NoRFIItems';
import RFIListHeaderCell from './RFIListHeaderCell';
import RFIListTable from './RFIListTable';

// ID / Subject / Status / Assignee / Due Date
const gridTemplateColumns = 'minmax(72px, 80px) 2.5fr 170px 170px 2fr 1fr 1fr';

interface Props extends WithAppStoreProps, RouteComponentProps {
  onCreateRFI: () => void;
}

const RFIList = (props: Props) => {
  const [openRows, setOpenRows] = useState<List<string>>(List<string>());
  const { height: bannerHeight } = useContext(BannerContext);
  const { appState, history, location, actions } = props;
  const { rfis, queriedRFIs, queriedRFIGroups, interactions, sheets } =
    appState;

  const getTemplate = (): ReactElement => {
    const { translate } = useTranslator(['rfiPage']);
    const handleRfiRowClick = (rfiId: string, rfiNumber: number): void => {
      if (rfiNumber === -1) {
        actions.deleteInteractions('ViewRFI');
        draftRowClick(rfiId);
      } else {
        actions.deleteInteractions('CreateRFI');
        rfiRowClick(rfiId);
      }
    };

    const rfiRowClick = (rfiId: string): void => {
      const isDraftingAnRFI = appState.interactions.some((i) =>
        ['CreateRFI'].includes(i.type)
      );
      if (
        isDraftingAnRFI &&
        appState.viewRFI.toString() !== appState.viewEdittedRFI.toString()
      ) {
        unsavedChangesModal(rfiRowAccept, rfiId);
      } else {
        rfiRowAccept(rfiId);
      }
    };

    const rfiRowAccept = (rfiId: string): void => {
      actions.deleteInteractions('CreateRFI');
      const nextLocation = {
        ...location,
        search: `?rfi=${encodeURIComponent(rfiId)}`,
      };

      history.replace(nextLocation);
    };

    const draftRowClick = (rfiId: string): void => {
      const isDraftingAnRFI = appState.interactions.some((i) =>
        ['CreateRFI'].includes(i.type)
      );
      if (
        isDraftingAnRFI &&
        appState.viewRFI.toString() !== appState.viewEdittedRFI.toString()
      ) {
        unsavedChangesModal(draftRowAccept, rfiId);
      } else {
        draftRowAccept(rfiId);
      }
    };

    const draftRowAccept = (rfiId: string): void => {
      const selectedRfi = appState.rfis.get(rfiId) || new RFI();
      clearQueryParam('rfi', history);
      const interaction = new CreateRFI({
        rfi: selectedRfi.toNewRFI(),
        fromDraft: true,
        rfiid: selectedRfi.id,
        attachments: selectedRfi.attachments,
      });

      const currentlyOpenRFIInteraction = appState.interactions.find((i) =>
        ['CreateRFI'].includes(i.type)
      );
      if (currentlyOpenRFIInteraction) {
        actions.updateInteraction(interaction);
      } else {
        actions.createInteraction(interaction);
      }
    };

    const unsavedChangesModal = (
      acceptFunction: Function,
      rfiId: string
    ): void => {
      const modalState = new ModalState({
        id: 'launchDraftChangesRFIDraftDialog',
        portal: 'main',
        open: true,
        overlayClassName: 'modal__overlay',
        props: {
          headerText: 'Are you sure?',
          content:
            'Unsaved changes will be lost if you cancel. Are you sure you wish to continue?',
          denyCallback: (): void => {
            actions.clearAllModals();
          },
          acceptCallback: (): void => {
            actions.setViewRFI(new NewRFI());
            actions.setViewEdittedRFI(new NewRFI());
            actions.clearAllModals();
            actions.deleteInteractions('CreateRFI');
            acceptFunction(rfiId);
          },
          xClickCallback: (): void => {
            actions.setViewRFI(new NewRFI());
            actions.setViewEdittedRFI(new NewRFI());
            actions.clearAllModals();
            actions.deleteInteractions('CreateRFI');
            acceptFunction(rfiId);
          },
        },
        component: ConfirmModal,
      });
      actions.createModal(modalState);
    };

    const handleRowToggle = (rowId: string): void => {
      const updatedRows = openRows.includes(rowId)
        ? openRows.filter((item) => item !== rowId)
        : openRows.push(rowId);

      setOpenRows(updatedRows);
    };

    const currentlyOpenViewRFI = (): string => {
      if (interactions.some((i) => i.type === 'ViewRFI')) {
        const { rfi } = interactions.find(
          (i) => i.type === 'ViewRFI'
        ) as ViewRFI;
        return rfi.id;
      }
      if (interactions.some((i) => i.type === 'CreateRFI')) {
        const { rfiid } = interactions.find(
          (i) => i.type === 'CreateRFI'
        ) as CreateRFI;
        return rfiid || '';
      }
      return '';
    };

    const openRfi = currentlyOpenViewRFI();

    return (
      <div style={{ height: `calc(100vh - 153px - ${bannerHeight}px)` }}>
        <StdList id="rfi-list" gridTemplateColumns={gridTemplateColumns}>
          <StdListHeader>
            <RFIListHeaderCell sortCell text="ID" field="number" />
            <RFIListHeaderCell
              leftBorder
              sortCell
              text={translate('rfiPage:subject')}
              field="title"
            />
            <RFIListHeaderCell
              sortCell
              leftBorder
              text={translate('rfiPage:status')}
              field="status"
            />
            <RFIListHeaderCell
              sortCell
              leftBorder
              text={translate('rfiPage:postedSheets')}
              field="sheets"
            />
            <RFIListHeaderCell
              sortCell
              leftBorder
              text={translate('rfiPage:assignee')}
              field="assignee"
            />
            <RFIListHeaderCell
              sortCell
              leftBorder
              text={translate('rfiPage:dateCreated')}
              field="craeteddate"
            />
            <RFIListHeaderCell
              leftBorder
              sortCell
              text={translate('rfiPage:dueDate')}
              field="duedate"
            />
          </StdListHeader>
          <StdListBody>
            {rfis.size === 0 ? (
              <NoRFIItems onCreateRFI={props.onCreateRFI} />
            ) : (
              <ExpandableTableRowContext.Provider
                value={{
                  currentlyOpenInViewer: openRfi,
                  onRowToggle: (rowId: string) => handleRowToggle(rowId),
                  onRowClick: (rfiId: string, rfiNumber: number) =>
                    handleRfiRowClick(rfiId, rfiNumber),
                  openRows,
                }}
              >
                <RFIListTable
                  rfis={rfis}
                  queriedRFIs={queriedRFIs}
                  queriedRFIGroups={queriedRFIGroups}
                  interactions={interactions}
                  sheets={sheets}
                />
              </ExpandableTableRowContext.Provider>
            )}
          </StdListBody>
        </StdList>
      </div>
    );
  };

  return getTemplate();
};

export default withRouter(withAppStore<Props>(RFIList));
